/**
 * Шапка приложения. Макет data/Задачник.dc.html, строки 98–120.
 *
 * §3.4: шапка НЕ размонтируется при переходах — она живёт в AppShell,
 * а маршруты приходят в Outlet. Высота фиксирована, чтобы контент
 * не прыгал при смене раздела.
 *
 * Разделы: Задачник, Отчётность, Заметки, Отпуска, Администрирование. Последний виден
 * только при наличии административных прав — но сервер проверяет независимо.
 *
 * Раскладки (docs/08-mobile-roadmap.md, M1): на телефоне вкладки разделов
 * уходят в нижнюю панель (BottomNav), на телефоне и планшете поле поиска
 * сворачивается в кнопку, открывающую поиск на весь экран.
 */

import { Eye, LogOut, User } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useLogoutMutation } from "@/features/auth/api";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { api, getWsClient, sessionLost } from "@taskflow/client-core";
import { httpLogout } from "@/shared/auth/httpAuth";
import { Avatar, cn, fullNameOf, Menu } from "@/shared/ui";

import { GlobalSearch, GlobalSearchButton } from "./GlobalSearch";
import { isSectionActive, useAppSections } from "./sections";
import { taskbookLink } from "./taskbookLink";

export function AppHeader() {
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const taskbook = taskbookLink(location.pathname, location.search);
  const sections = useAppSections();

  const signOut = async () => {
    // Порядок важен: сперва отзываем сессию на сервере, потом рвём сокет
    // и чистим кэш. Обратный порядок оставил бы живой refresh-токен.
    try {
      await logout().unwrap();
    } catch {
      // Даже если запрос не дошёл, локальную сессию гасим — §4.2.
    }
    await httpLogout();
    getWsClient().disconnect();
    dispatch(api.util.resetApiState());
    dispatch(sessionLost(null));
    void navigate("/login", { replace: true });
  };

  return (
    // Высота — из --header-h (index.css): от неё же считают высоту экраны
    // во всю высоту окна, например доска. Вырез сверху (PWA) — отступом.
    <header className="sticky top-0 z-30 flex h-(--header-h) shrink-0 items-center gap-3 border-b border-line bg-surface px-4 pt-(--safe-top) lg:gap-6 lg:px-5">
      <NavLink
        to={taskbook.to}
        onClick={taskbook.stay ? (event) => event.preventDefault() : undefined}
        className="shrink-0 text-[15px] font-extrabold tracking-tight text-fg"
      >
        MYTASKFLOW
      </NavLink>

      <nav
        className="flex shrink-0 items-center gap-1 max-sm:hidden"
        aria-label="Основные разделы"
      >
        {sections.map((section) => (
          <HeaderTab
            key={section.key}
            to={section.to}
            stay={section.stay}
            label={section.label}
            sections={section.sections}
          />
        ))}
      </nav>

      <div className="min-w-0 flex-1 max-lg:hidden">
        <GlobalSearch />
      </div>
      <div className="flex-1 lg:hidden" />

      <div className="flex shrink-0 items-center gap-1.5">
        <GlobalSearchButton className="lg:hidden" />
        <NotificationBell />

        {user !== null && (
          <Menu
            trigger={
              <button
                type="button"
                aria-label={`Меню профиля: ${fullNameOf(user)}`}
                className="rounded-full focus-visible:outline-2 focus-visible:outline-accent"
              >
                <Avatar person={user} size="md" />
              </button>
            }
            actions={[
              {
                key: "profile",
                label: "Профиль и уведомления",
                icon: User,
                onSelect: () => void navigate("/settings/profile"),
              },
              {
                key: "subscriptions",
                label: "Мои подписки",
                icon: Eye,
                onSelect: () => void navigate("/settings/subscriptions"),
              },
              {
                key: "logout",
                label: "Выйти",
                icon: LogOut,
                danger: true,
                separated: true,
                onSelect: () => void signOut(),
              },
            ]}
          />
        )}
      </div>
    </header>
  );
}

/**
 * Вкладка раздела. Активна на всех адресах раздела: «Задачник» — это и
 * /tasks, и доски /boards/…. Прежде подсветка бралась только из адреса
 * ссылки, а /tasks сразу открывает доску, и на доске «Задачник» не был
 * подчёркнут.
 */
function HeaderTab({
  to,
  label,
  sections,
  stay,
}: {
  to: string;
  label: string;
  sections: string[];
  /** Уже на месте: нажатие не должно пересоздавать экран. */
  stay: boolean;
}) {
  const { pathname } = useLocation();
  const active = isSectionActive(pathname, sections);
  return (
    <Link
      to={to}
      onClick={stay ? (event) => event.preventDefault() : undefined}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative px-2 py-2 text-[13px] font-semibold transition-colors lg:px-3",
        active ? "text-fg" : "text-muted hover:text-fg",
      )}
    >
      {label}
      {active && (
        <span className="absolute right-2 bottom-0 left-2 h-0.5 bg-accent lg:right-3 lg:left-3" aria-hidden />
      )}
    </Link>
  );
}
